import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Folder } from '@/types/task';
import { useToast } from '@/hooks/use-toast';

export function useFolders() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchFolders = useCallback(async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from('folders' as any)
      .select('*')
      .eq('user_id', user.id)
      .order('order_index', { ascending: true });

    if (error) {
      console.error('Error fetching folders:', error);
    } else {
      setFolders((data as any[]) ?? []);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  const addFolder = useCallback(async (name: string, color: string = '#6366f1') => {
    if (!user) return null;
    const newFolder = {
      user_id: user.id,
      name,
      color,
      order_index: folders.length,
    };
    const { data, error } = await supabase
      .from('folders' as any)
      .insert(newFolder)
      .select()
      .single();

    if (error) {
      toast({ title: 'Error creating folder', variant: 'destructive' });
      return null;
    }
    const folder = data as any as Folder;
    setFolders(prev => [...prev, folder]);
    return folder;
  }, [user, folders.length, toast]);

  const updateFolder = useCallback(async (id: string, updates: Partial<Pick<Folder, 'name' | 'color'>>) => {
    const { error } = await supabase
      .from('folders' as any)
      .update(updates)
      .eq('id', id);

    if (error) {
      toast({ title: 'Error updating folder', variant: 'destructive' });
      return;
    }
    setFolders(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
  }, [toast]);

  const deleteFolder = useCallback(async (id: string) => {
    const { error } = await supabase
      .from('folders' as any)
      .delete()
      .eq('id', id);

    if (error) {
      toast({ title: 'Error deleting folder', variant: 'destructive' });
      return;
    }
    setFolders(prev => prev.filter(f => f.id !== id));
  }, [toast]);

  const reorderFolders = useCallback(async (reordered: Folder[]) => {
    setFolders(reordered);
    const updates = reordered.map((f, i) => ({ id: f.id, order_index: i, user_id: f.user_id, name: f.name, color: f.color }));
    for (const u of updates) {
      await supabase.from('folders' as any).update({ order_index: u.order_index }).eq('id', u.id);
    }
  }, []);

  return { folders, loading, addFolder, updateFolder, deleteFolder, reorderFolders };
}
